// @flow
import React, { Fragment } from 'react';
import { AppLink } from '@finect/tabular-core/AppLink';
import { Link } from 'react-router-dom';
import { Head } from '@finect/tabular-components/Head';
import { H1, H2 } from '@finect/tabular-core/Headings';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { DatalayerScene } from '@finect/front-gtm';
import { withParams } from '@finect/front-core/withParams';
import type { WithParamsProps } from '@finect/front-core/withParams';
import { SkeletonLine } from '@finect/tabular-components/Skeleton';
import { BottomAds } from '@finect/tabular-components/Ads';
import { buildOpenGraph, buildTwitter } from '../../data/seo';
import { ADVISERS_PER_PAGE as PER_PAGE, fetchAdvisers } from '../../data/advisers';
import {
  AdvisersPager,
  BottomNavigation,
  InsideNavigation,
  LazyPill,
  FaqItem,
  FaqSection,
  ContentBlocksSection,
  ContentIntro,
  ListHeader,
  MainGrey,
  PartnersDisclaimer
} from '../../components';
import type { NewProfessionalType } from '../../types';
import { ProfessionalCard } from './components/ProfessionalCard';
import { LeadBanner } from './components/LeadBanner';
import { LeadBar } from './components/LeadBar';
import { ListDisclaimer, SceneInner, PillArea } from './partials';
import { CUSTOM_PROVINCES, PROVINCES } from './constants';

// Quita todos los encabezados h1-h6 (para intro y respuestas FAQ).
const stripAllHeadings = (html: string): string =>
  (html || '').replace(/<\/?h[1-6](?:\s[^>]*)?>/gi, '');

// Quita solo los encabezados no permitidos (h1, h4-h6). Mantiene h2/h3 del contenido editorial.
const stripDisallowedHeadings = (html: string): string =>
  (html || '').replace(/<\/?h(?:1|[4-6])(?:\s[^>]*)?>/gi, '');

// Separa el HTML en [antes del primer heading, desde el primer heading] para intercalar la
// píldora justo antes del primer h2/h3 del contenido editorial. Si no hay heading, todo va
// debajo de la píldora (así no queda enterrada).
const splitBeforeFirstHeading = (html: string): [string, string] => {
  const str = html || '';
  const index = str.search(/<h[23][\s>]/i);
  if (index === -1) return ['', str];
  return [str.slice(0, index), str.slice(index)];
};

type Props = {
  staticContext?: Object,
  history: Object,
  match: Object,

  toggleModal: Function
} & WithParamsProps;

type State = {
  fetching: boolean,
  professionals: Array<NewProfessionalType>,
  paging: Object,
  page: number,
  offset: number | null,
  initialPage: number,
  setting: ?Object,
  showLeadBar: boolean,
  fetched: boolean,
  derived: Object
};

const getPagePath = (params: Object, page?: number) => {
  const filters = { ...page && page > 1 ? { page } : {} };

  const queryString = Object.keys(filters)
    .map((key: any) => `${key}=${filters[key]}`)
    .join('&');

  return `/asesores/encuentra-un-asesor/en/${params.province}${queryString ? `?${queryString}` : ''}`;
};

// Scroll suave hasta la píldora de captación embebida al pie de la página.
const scrollToPill = () => {
  if (typeof document === 'undefined') return;

  const target = document.getElementById('solicita-asesor');
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

// Todo lo que se deriva de los datos de la escena y no del scroll: el nombre de la
// provincia, el orden del listado, el HTML editorial ya limpio y los dos JSON-LD.
// Vive fuera del render (que debe ser puro) y se recalcula solo cuando cambia alguna
// de sus entradas; el toggle de la LeadBar al hacer scroll ya no arrastra nada de esto.
// ItemList del listado. Variante "todo en uno": los datos van embebidos y SIN `url`,
// porque desde aquí no se enlaza a las fichas; declarar URLs que la página no enlaza es
// justo lo que provoca avisos de indexación. Se omite si no hay a quién listar.
const buildItemListSchema = (professionals, provinceName, startPosition) => {
  const listables = professionals.filter(professional => professional.displayName);

  if (!listables.length) return null;

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Asesores financieros en ${provinceName}`,
    numberOfItems: listables.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: listables.map((professional, index) => {
      const [organization = {}] = professional.organizations || [];
      const organizationName = organization.name || organization.displayName;
      // Solo campos con valor: un string vacío es schema mal formado.
      const person: Object = { '@type': 'Person', name: professional.displayName };

      if (professional.tagline) person.jobTitle = professional.tagline;
      if (organizationName) person.worksFor = { '@type': 'Organization', name: organizationName };

      return { '@type': 'ListItem', position: startPosition + index, item: person };
    })
  });
};

const buildDerived = ({ setting, professionals, provinceSlug, initialPage }: Object): Object => {
  const customProvince = CUSTOM_PROVINCES.find(({ slug }) => slug === provinceSlug);
  const province = PROVINCES.find(({ slug }) => slug === provinceSlug);
  const provinceName = customProvince
    ? customProvince.province
    : province
      ? province.province
      : provinceSlug;

  // El "asesor del mes" (isHighlighted) se muestra destacado al principio del listado.
  const orderedProfessionals = [
    ...professionals.filter(professional => professional.isHighlighted),
    ...professionals.filter(professional => !professional.isHighlighted)
  ];

  const [contentBefore, contentAfter] = splitBeforeFirstHeading(
    setting?.content ? stripDisallowedHeadings(setting.content) : ''
  );

  const faqs = Array.isArray(setting?.faqs)
    ? setting.faqs.map(faq => ({ ...faq, answer: stripAllHeadings(faq.answer) }))
    : [];

  return {
    setting,
    professionals,
    provinceSlug,
    initialPage,
    provinceName,
    orderedProfessionals,
    // Las posiciones continúan desde la página por la que se entró, para que
    // /en/madrid y /en/madrid?page=2 no declaren ambas la posición 1.
    itemListSchema: buildItemListSchema(orderedProfessionals, provinceName, (initialPage - 1) * PER_PAGE + 1),
    introHtml: setting?.intro ? stripAllHeadings(setting.intro) : '',
    contentBefore,
    contentAfter,
    faqs,
    // El JSON-LD de las FAQ barre con regex el HTML de cada respuesta: era, con
    // diferencia, lo más caro de repetir en cada render.
    faqSchema: faqs.length > 0 ? JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer.replace(/<[^>]*>?/gm, '')
        }
      }))
    }) : null,
    breadcrumbSchema: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Inicio',
          item: 'https://www.finect.com'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Asesores financieros',
          item: 'https://www.finect.com/asesores'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: `Asesores financieros en ${provinceName}`,
          item: `https://www.finect.com${getPagePath({ province: provinceSlug })}`
        }
      ]
    })
  };
};

class Province extends React.Component<Props, State> {
  static loadInitialData(store, cookies, match, params) {
    const offset = params.page > 0 ? (params.page - 1) * PER_PAGE : null;
    const province = match.params ? match.params.province : params.params.province;

    const loadAdvisers = fetchAdvisers({
      province,
      offset,
      onSuccess: (professionals: Object) => professionals,
      onError: () => console.log('LID: Request to advisers/people failed!')
    });

    const loadSetting = finectAPI({
      version: 'v4',
      path: `resources/setting/advisers-province-${province}`,
      method: 'GET',
      onSuccess: (setting: Object) => setting,
      onError: () => null
    });

    return Promise.all([loadAdvisers, loadSetting]).then(([professionals, setting]) => ({
      professionals: professionals?.data || [],
      paging: professionals?.paging || {},
      setting: setting?.data?.value
    }));
  }

  // Recalcula lo derivado solo cuando cambian sus entradas. Al vivir aquí y no en
  // render, no se muta nada durante la fase de render.
  static getDerivedStateFromProps(props: Object, state: Object) {
    const provinceSlug = props.match.params.province;
    const { setting, professionals, initialPage, derived } = state;

    if (
      derived &&
      derived.setting === setting &&
      derived.professionals === professionals &&
      derived.provinceSlug === provinceSlug &&
      derived.initialPage === initialPage
    ) return null;

    return { derived: buildDerived({ setting, professionals, provinceSlug, initialPage }) };
  }

  state =
    typeof window === 'undefined'
      ? {
        fetching: false,
        professionals: (this.props.staticContext && this.props.staticContext.initialData.initial.professionals) || [],
        paging: (this.props.staticContext && this.props.staticContext.initialData.initial.paging) || {},
        page: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
        offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : 0,
        initialPage: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
        setting: (this.props.staticContext && this.props.staticContext.initialData.initial.setting) || null,
        showLeadBar: false,
        fetched: Boolean(this.props.staticContext && this.props.staticContext.initialData.initial),
        // Lo rellena getDerivedStateFromProps antes del primer render: aquí solo se declara
        // para que el estado esté completo desde la construcción.
        derived: {}
      }
      : {
        fetching: false,
        professionals:
          (window.initialData && window.initialData.initial && window.initialData.initial.professionals) || [],
        paging: (window.initialData && window.initialData.initial && window.initialData.initial.paging) || {},
        page: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
        offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : 0,
        initialPage: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
        setting: (window.initialData && window.initialData.initial && window.initialData.initial.setting) || null,
        showLeadBar: false,
        fetched: Boolean(window.initialData && window.initialData.initial),
        derived: {}
      };

  mounted: boolean = false;

  scrollFrame: number | null = null;

  componentDidMount() {
    this.mounted = true;

    window && window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Con datos del SSR no hay que volver a pedir nada, aunque la provincia no tenga asesores.
    if (!this.state.fetched) {
      this.setState({ fetching: true });
      Province.loadInitialData('', {}, this.props.match, this.props.params)
        .then(({ professionals, paging, setting }) => {
          if (!this.mounted) return;

          this.setState({
            professionals,
            paging,
            setting,
            fetched: true,
            page: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
            offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : null
          });
        })
        .catch(() => {
          // Aunque falle, se sale del esqueleto: se pinta el título genérico, que para eso
          // el nombre de la provincia ya no depende de ninguna petición.
          if (this.mounted) this.setState({ fetched: true });
        })
        .then(() => {
          if (this.mounted) this.setState({ fetching: false });
        });
    }
  }

  componentWillUnmount() {
    this.mounted = false;

    window && window.removeEventListener('scroll', this.handleScroll);

    if (this.scrollFrame !== null) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  redirectTo = (url: string) => {
    this.props.history.push(url);
  };

  loadNextPage = () => {
    this.setState({ fetching: true });
    const { page } = this.state;
    const { province } = this.props.match.params;
    const nextPage = parseInt(page, 10) + 1;

    fetchAdvisers({
      province,
      offset: (nextPage - 1) * PER_PAGE,
      onSuccess: ({ data, paging }: Object) => {
        if (!this.mounted) return;

        this.setState({
          professionals: [...this.state.professionals, ...data],
          paging,
          page: nextPage,
          offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : null
        });
      },
      onError: () => { }
    }).then(() => {
      if (this.mounted) this.setState({ fetching: false });
    });
  };

  // El evento scroll se dispara varias veces por frame y la medición lee
  // offsetHeight/getBoundingClientRect, que fuerzan layout. Con requestAnimationFrame
  // se mide como mucho una vez por frame, y el listener es pasivo para no bloquear
  // el scroll del navegador.
  handleScroll = () => {
    if (this.scrollFrame !== null) return;

    this.scrollFrame = window.requestAnimationFrame(this.measureScroll);
  };

  measureScroll = () => {
    this.scrollFrame = null;

    if (typeof document === 'undefined') return;

    if (!document.documentElement) return;

    const OFFSET = 700;
    if (document.documentElement.offsetHeight - window.scrollY - window.innerHeight < OFFSET) {
      if (
        !this.state.fetching &&
        this.state.professionals.length &&
        this.state.paging.links.find(link => link.rel === 'next')
      ) {
        this.loadNextPage();
      }
    }

    // La barra sticky de captación aparece cuando el formulario embebido queda por encima
    // del viewport (el usuario ha bajado más allá de él).
    const pill = document.getElementById('solicita-asesor');
    if (pill) {
      const scrolledPastPill = pill.getBoundingClientRect().bottom < 0;
      if (scrolledPastPill !== this.state.showLeadBar) {
        this.setState({ showLeadBar: scrolledPastPill });
      }
    }
  };

  handleLoadMoreClick = () => {
    this.loadNextPage();
  };

  render() {
    const { professionals, paging, fetching, page, showLeadBar, fetched } = this.state;
    const { match, toggleModal } = this.props;

    const prevPath = getPagePath(match.params, page - 1);
    const nextPath = getPagePath(match.params, page + 1);
    const currentPath = getPagePath(match.params);
    // El canonical apunta a ESTA página: si todas apuntaran a la primera, los asesores
    // a partir del 21 quedarían declarados como duplicado y nunca se indexarían.
    const canonicalPath = getPagePath(match.params, page);

    const paginationReady = paging.hasOwnProperty('totalItems');
    const pages = Math.ceil(paging.totalItems / paging.limit);

    const { setting } = this.state;
    const {
      provinceName, orderedProfessionals, introHtml, contentBefore, contentAfter,
      faqs, faqSchema, breadcrumbSchema, itemListSchema
    } = this.state.derived;

    const h1Content = setting?.h1 || `Asesores financieros en ${provinceName}`;
    const metaTitle = setting?.metaTitle || `Asesores financieros en ${provinceName} - Finect Asesores`;
    const metaDescription = setting?.metaDescription || `Encuentra un asesor financiero de confianza en ${provinceName} que te ayude a planificar tus finanzas para conseguir tus objetivos.`;

    const intro = setting?.intro;

    // H4 — Provincias sin asesores Y sin contenido editorial son thin/doorway:
    // las marcamos noindex,follow. Las vacías-pero-curadas (tienen `setting`) y las
    // que sí listan asesores siguen siendo index,follow.
    const totalAdvisers = paginationReady ? paging.totalItems : professionals.length;
    const isEmptyProvince = totalAdvisers === 0;
    const robotsValue = isEmptyProvince && !setting ? 'noindex, follow' : 'index, follow';

    return (
      <Fragment>
        <InsideNavigation
          links={[
            {
              title: 'Empresas',
              path: '/asesores/encuentra-un-asesor/empresas'
            }
          ]}
          currentPath={currentPath}
          buttonText="¿Necesitas ayuda?"
          redirectTo={this.redirectTo}
          toggleModal={toggleModal}
        />

        <MainGrey minHeight={!professionals.length}>
          <Head
            title={metaTitle}
            description={metaDescription}
            robots={robotsValue}
            canonical={canonicalPath}
            withoutName
            openGraph={buildOpenGraph({ title: metaTitle, description: metaDescription, url: currentPath })}
            twitter={buildTwitter({ title: metaTitle, description: metaDescription })}
          >
            {/* Sin Fragment: react-helmet descarta en silencio los hijos envueltos en uno. */}
            {paginationReady && page > 1 ? <link rel="prev" href={prevPath} /> : null}
            {paginationReady && pages > page ? <link rel="next" href={nextPath} /> : null}
            <script type="application/ld+json">
              {breadcrumbSchema}
            </script>
            {faqSchema && (
              <script type="application/ld+json">
                {faqSchema}
              </script>
            )}
            {itemListSchema && (
              <script type="application/ld+json">
                {itemListSchema}
              </script>
            )}
          </Head>

          <DatalayerScene pageType="advisersProfessionals" />

          <SceneInner>
            <ListHeader>
              {/* El h1 definitivo depende del `setting` editorial. Hasta que se sabe si lo
                  hay, un esqueleto de la misma altura: pintar un título provisional y
                  sustituirlo después es un salto de texto feo. Con SSR `fetched` ya es
                  true en el primer render, así que el h1 va en el HTML servido. */}
              {fetched ? (
                <H1 large>{h1Content}</H1>
              ) : (
                <SkeletonLine style={{ width: '100%', maxWidth: '420px', height: '50px' }} />
              )}
            </ListHeader>
            {/* Intro completo + entradilla del contenido (todo lo previo al primer heading). */}
            {intro ? (
              <ContentIntro dangerouslySetInnerHTML={{ __html: introHtml }} />
            ) : null}
            {!intro && !isEmptyProvince ? (
              <ContentIntro>
                <p>
                  Encuentra <b>asesores financieros en {provinceName}</b>. Estos son los {totalAdvisers}{' '}
                  profesionales que pueden ayudarte a planificar tus finanzas e inversiones, ya sea de forma
                  presencial en {provinceName} o en remoto.
                </p>
              </ContentIntro>
            ) : null}
            {contentBefore ? (
              <ContentBlocksSection dangerouslySetInnerHTML={{ __html: contentBefore }} />
            ) : null}

            {/* Píldora: en el primer scroll, antes del primer heading del contenido (punto 1). */}
            <PillArea>
              <LazyPill />
            </PillArea>

            {/* Contenido editorial (SEO), desde el primer heading, debajo de la píldora. */}
            {contentAfter ? (
              <ContentBlocksSection
                aria-label="Información sobre asesores financieros en la provincia"
                dangerouslySetInnerHTML={{ __html: contentAfter }}
              />
            ) : null}

            {!fetching ? null : <SkeletonLine style={{ width: '100%', height: '600px' }} />}

            {!professionals.length && !fetching ? (
              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <p>No hay asesores en esta provincia</p>
                <p>
                  <AppLink as={Link} to="/asesores/encuentra-un-asesor/profesionales">
                    Ir al listado completo
                  </AppLink>
                </p>
              </div>
            ) : null}

            {orderedProfessionals.length ? (
              <ListDisclaimer>
                <PartnersDisclaimer note="Este listado no constituye una recomendación personalizada." />
              </ListDisclaimer>
            ) : null}

            {orderedProfessionals.map((professional, index) => {
              // CTA de captación integrado: antes del primer asesor y, a partir de ahí, cada
              // 3 resultados. (En móvil/tablet el acceso permanente lo cubre la LeadBar.)
              const showBanner = index % 3 === 0;

              return (
                <Fragment key={professional.id}>
                  {showBanner ? <LeadBanner onClick={scrollToPill} /> : null}
                  <ProfessionalCard index={index} {...professional} />
                </Fragment>
              );
            })}

            <AdvisersPager
              paging={paging}
              page={page}
              fetching={fetching}
              nextPath={nextPath}
              onLoadMore={this.handleLoadMoreClick}
            />

            {faqs.length > 0 && (
              <FaqSection>
                <div className="faq-header">
                  <H2>Preguntas frecuentes</H2>
                </div>
                {faqs.map((faq, index) => (
                  <FaqItem key={index} title={faq.question} path="">
                    <div
                      className="faq-answer"
                      id={faq.anchor ? faq.anchor.replace('#', '') : undefined}
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </FaqItem>
                ))}
              </FaqSection>
            )}
          </SceneInner>

          <BottomAds path="Finect_Content_1" sticky closeable />
        </MainGrey>

        <BottomNavigation
          links={[
            { title: 'Asesores', path: '/asesores' },
            {
              title: 'Encuentra un asesor',
              path: '/asesores/encuentra-un-asesor/empresas'
            },
            { title: 'Profesionales' }
          ]}
        />

        <LeadBar visible={showLeadBar} onClick={scrollToPill} />
      </Fragment >
    );
  }
}

export default withParams(Province);
